import { Link } from "react-router-dom";
import { ArrowRight, Trees, Sparkles, Sun } from "lucide-react";

/**
 * "Vous avez un mobile home ?", promo block driving traffic to a dedicated
 * mobile home conciergerie page.
 *
 * Props:
 *  - variant      : "dark" (default) | "light"
 *  - cityName     : ville cible affichée dans le titre  (default "Dinard")
 *  - targetUrl    : URL de la page dédiée               (default "/mobil-home-dinard")
 *  - domainLine   : phrase italique entre la ville et le mobile home, incluant
 *                   la préposition (ex. "au Domaine du Bois d'Amour", "dans un camping")
 */
const MobilHomePromo = ({
  variant = "dark",
  cityName = "Dinard",
  targetUrl = "/mobil-home-dinard",
  domainLine = "au Domaine du Bois d'Amour",
  region = "la Côte d'Émeraude",
}) => {
  const props = { cityName, targetUrl, domainLine, region };
  if (variant === "light") return <PromoLight {...props} />;
  return <PromoDark {...props} />;
};

const PromoLight = ({ cityName, targetUrl, domainLine, region }) => {
  return (
    <section
      className="py-20 lg:py-28 bg-white"
      data-testid="services-mobilhome-promo"
    >
      <div className="container-tm">
        <div className="relative bg-cream/40 border border-gold/20 rounded-3xl overflow-hidden shadow-soft">
          <div className="grid lg:grid-cols-12 gap-0 items-stretch">
            {/* Left, image */}
            <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto lg:min-h-[460px]">
              <img
                src="/images/mobil-home.jpg"
                alt="Mobile home haut de gamme à Dinard, Domaine du Bois d'Amour, géré par Terre et Mer Conciergerie"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <span className="absolute top-6 left-6 px-3 py-1.5 rounded-full bg-gold text-ink text-[10px] uppercase tracking-[0.22em] font-semibold shadow-soft">
                Nouveau
              </span>
            </div>

            {/* Right, copy */}
            <div className="lg:col-span-7 p-8 md:p-12 lg:p-14 flex flex-col justify-center">
              <span className="overline inline-flex items-center gap-2 text-gold">
                <Trees size={12} strokeWidth={2} />
                Aussi : conciergerie mobile home
              </span>

              <h2 className="mt-5 font-serif font-light text-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight">
                Vous avez un mobile home
                <br />
                <span className="italic text-gold">{domainLine}</span>
                <br />à {cityName} ?
              </h2>

              <p className="mt-6 font-sans text-base md:text-lg text-ink/65 leading-relaxed max-w-xl">
                Bonne nouvelle : nous prenons aussi en charge la conciergerie de votre
                résidence mobile dans tous les domaines et campings de {region}.
                Mise en ligne sous 24 h, ménage hôtelier, accueil 24h/24
                et même la gestion de la sous-location auprès du domaine.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
                <MiniBadge
                  variant="light"
                  icon={Sparkles}
                  value="15 %"
                  label="commission unique"
                />
                <MiniBadge
                  variant="light"
                  icon={Sun}
                  value="13–22 K€"
                  label="revenu typique / saison"
                />
              </div>

              <Link
                to={targetUrl}
                className="btn-accent group mt-9 inline-flex"
                data-testid="services-mobilhome-cta"
              >
                Découvrir le service mobile home
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PromoDark = ({ cityName, targetUrl, domainLine, region }) => {
  return (
    <section
      className="py-20 lg:py-28 bg-cream"
      data-testid="services-mobilhome-promo"
    >
      <div className="container-tm">
        <div
          className="relative rounded-3xl overflow-hidden grain-overlay shadow-lift"
          style={{
            background:
              "linear-gradient(135deg, #0F3D52 0%, #0A2A39 60%, #0F3D52 100%)",
          }}
        >
          {/* Decorative blurs */}
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-gold/20 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-[320px] h-[320px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-0 items-stretch">
            {/* Left, image */}
            <div className="lg:col-span-5 relative aspect-[4/3] lg:aspect-auto lg:min-h-[480px]">
              <img
                src="/images/mobil-home.jpg"
                alt="Mobile home haut de gamme à Dinard, Domaine du Bois d'Amour, géré par Terre et Mer Conciergerie"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-ink/60 via-ink/20 to-transparent pointer-events-none" />
              <span className="absolute top-6 left-6 px-3 py-1.5 rounded-full bg-gold text-ink text-[10px] uppercase tracking-[0.22em] font-semibold">
                Nouveau
              </span>
            </div>

            {/* Right, copy */}
            <div className="lg:col-span-7 p-8 md:p-12 lg:p-14 text-white flex flex-col justify-center">
              <span className="overline text-gold inline-flex items-center gap-2">
                <Trees size={12} strokeWidth={2} />
                Aussi : conciergerie mobile home
              </span>

              <h2 className="mt-5 font-serif font-light text-white text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight">
                Vous avez un mobile home
                <br />
                <span className="italic text-gold">{domainLine}</span>
                <br />à {cityName} ?
              </h2>

              <p className="mt-6 font-sans text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
                Bonne nouvelle : nous prenons en charge la conciergerie de votre résidence mobile dans tous les domaines et campings de {region}. Mise en ligne sous 24 h, ménage hôtelier, accueil 24h/24 et même la gestion de la sous-location auprès du domaine.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-4">
                <MiniBadge icon={Sparkles} value="15 %" label="commission unique" />
                <MiniBadge icon={Sun} value="13–22 K€" label="revenu typique / saison" />
              </div>

              <Link
                to={targetUrl}
                className="btn-accent group mt-9 inline-flex"
                data-testid="services-mobilhome-cta-dark"
              >
                Découvrir le service mobile home
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="ml-3 group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const MiniBadge = ({ icon: Icon, value, label, variant = "dark" }) => {
  const isLight = variant === "light";
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 ${
          isLight
            ? "bg-gold/12 border-gold/40"
            : "bg-gold/15 border-gold/30"
        }`}
      >
        <Icon size={15} strokeWidth={1.8} className="text-gold" />
      </div>
      <div>
        <p
          className={`font-serif text-base leading-none ${
            isLight ? "text-ink" : "text-white"
          }`}
        >
          {value}
        </p>
        <p
          className={`text-[10px] uppercase tracking-[0.2em] font-semibold mt-1 leading-none ${
            isLight ? "text-ink/55" : "text-white/55"
          }`}
        >
          {label}
        </p>
      </div>
    </div>
  );
};

export default MobilHomePromo;
